// ⌘K palette, ported from the prototype: global search (`/search`, added as documents arrive)
// plus jump-to commands contributed by every active module.
import { api, money, useAuth } from "@keel/core";
import { Icon, Pill } from "@keel/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { activePlugins } from "./modules";

interface Hit {
  kind: string;
  id: string;
  title: string;
  subtitle: string;
  status: string;
  amount: number | null;
}
interface Item {
  group: string;
  to: string;
  icon: string;
  title: string;
  mono?: string;
  meta?: ReactNode;
}

const HREF: Record<string, (h: Hit) => string> = {
  order: (h) => `/orders/${h.id}`,
  invoice: (h) => `/invoices/${h.id}`,
  bill: (h) => `/bills/${h.id}`,
  purchase_order: (h) => `/purchase-orders/${h.id}`,
  delivery: (h) => `/deliveries/${h.id}`,
  customer: (h) => `/customers/${h.id}`,
  supplier: (h) => `/suppliers/${h.id}`,
  product: (h) => `/products/${h.id}`,
  production_order: (h) => `/production-orders/${h.id}`,
  loan: (h) => `/loans/${h.id}`,
  asset: (h) => `/assets/${h.id}`,
  employee: (h) => `/people/${h.id}`,
};
const ICON: Record<string, string> = {
  order: "cart",
  invoice: "invoice",
  bill: "receipt",
  purchase_order: "boxes",
  delivery: "truck",
  customer: "users",
  supplier: "factory",
  product: "box",
  production_order: "cog",
  loan: "cash",
  asset: "building",
  employee: "users",
};
const GROUP: Record<string, string> = {
  order: "Documents",
  invoice: "Documents",
  bill: "Documents",
  purchase_order: "Documents",
  delivery: "Documents",
  customer: "Contacts",
  supplier: "Contacts",
  product: "Products",
  production_order: "Documents",
  loan: "Documents",
  asset: "Documents",
  employee: "Contacts",
};
const snake = (s: string) => s.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const { can, me } = useAuth();
  const nav = useNavigate();
  const ref = useRef<HTMLDialogElement>(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [sel, setSel] = useState(0);

  useEffect(() => {
    ref.current?.showModal();
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([]);
      return;
    }
    let live = true;
    const t = setTimeout(
      () =>
        api<Hit[]>(`/search?q=${encodeURIComponent(q.trim())}`)
          .then((r) => live && setHits(r))
          .catch(() => {}),
      120,
    );
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q]);

  const needle = q.trim().toLowerCase();
  const all: [string, string, string, string][] = [
    ["Overview", "/", "home", ""],
    ["Help", "/help", "help", ""],
    ["What's new", "/whats-new", "sparkle", ""],
    ...activePlugins(me.modules).flatMap((p) => p.commands ?? []),
  ];
  const cmds: Item[] = all
    .filter(([l, , , p]) => (!p || can(p)) && (!needle || l.toLowerCase().includes(needle)))
    .slice(0, needle ? 6 : 12)
    .map(([title, to, icon]) => ({ group: needle ? "Commands" : "Jump to", to, icon, title }));
  const found: Item[] = hits.map((h) => ({
    group: GROUP[h.kind] ?? "Results",
    to: HREF[h.kind]?.(h) ?? "/",
    icon: ICON[h.kind] ?? "box",
    title: ["product", "customer", "supplier", "employee"].includes(h.kind) ? h.title : h.subtitle,
    mono:
      h.kind === "product"
        ? h.subtitle
        : ["customer", "supplier", "employee"].includes(h.kind)
          ? undefined
          : h.title,
    meta: (
      <>
        {h.status && <Pill status={snake(h.status)} />}
        {h.amount != null && <span className="num">{money(h.amount)}</span>}
      </>
    ),
  }));
  const items = [...found, ...cmds];
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset selection when results change
  useEffect(() => setSel(0), [q, hits.length]);

  const go = (it: Item) => {
    onClose();
    if (it.to.startsWith("/docs")) window.open(it.to, "_blank");
    else nav(it.to);
  };
  let last = "";
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes the dialog natively
    <dialog
      ref={ref}
      className="palette"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="palette-input">
        <Icon name="search" size={18} />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search orders, invoices, customers, SKUs — or type a command"
          autoComplete="off"
          spellCheck={false}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setSel((s) => Math.min(s + 1, items.length - 1));
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setSel((s) => Math.max(s - 1, 0));
            }
            const it = items[sel];
            if (e.key === "Enter" && it) {
              e.preventDefault();
              go(it);
            }
          }}
        />
        <kbd>esc</kbd>
      </div>
      <div className="palette-results" role="listbox">
        {items.length === 0 && <div className="palette-empty">No matches</div>}
        {items.map((it, i) => {
          const head = it.group !== last ? <div className="palette-group">{it.group}</div> : null;
          last = it.group;
          return (
            <div key={`${it.group}:${it.to}:${it.title}`}>
              {head}
              <a
                className={`palette-item ${i === sel ? "sel" : ""}`}
                role="option"
                aria-selected={i === sel}
                href={it.to}
                onMouseMove={() => sel !== i && setSel(i)}
                onClick={(e) => {
                  e.preventDefault();
                  go(it);
                }}
              >
                <span className="pi-icon">
                  <Icon name={it.icon} />
                </span>
                <span className="pi-main">
                  {it.mono && <span className="mono">{it.mono}</span>}
                  {it.title}
                </span>
                <span className="pi-meta">{it.meta}</span>
              </a>
            </div>
          );
        })}
      </div>
    </dialog>
  );
}
